import { UseInfiniteQueryResult } from '@tanstack/react-query';
import lodash from 'lodash';
import * as React from 'react';

export default function useInfiniteScroll<T extends HTMLElement>(ref: T | null, callback: () => void) {
  const onScroll = React.useMemo(
    () => lodash.debounce(
      () => {
        if (ref && ref.scrollHeight - ref.scrollTop - ref.clientHeight <= 100) {
          callback();
        }
      },
      100,
    ),
    [callback, ref],
  );
  React.useEffect(() => {
    if (ref && ref.scrollHeight <= ref.clientHeight) {
      onScroll();
    }
  }, [ref, onScroll]);

  return { onScroll };
}
const FETCH_LIMIT = 5;
// eslint-disable-next-line no-unused-vars, no-shadow
export function useInfiniteScrollV3<TData, TError, T extends HTMLElement, TItem>(data: UseInfiniteQueryResult<TData, TError>, ref: T | null, getItems: (data: TData) => TItem[], fetchLimit = FETCH_LIMIT) {
  const [fetchCount, setFetchCount] = React.useState(0);

  const fetchNextPage = React.useCallback(
    () => {
      if (data.hasNextPage) {
        data.fetchNextPage();
        setFetchCount((prevCount) => prevCount + 1);
      }
    },
    [data],
  );

  const { onScroll } = useInfiniteScroll<T>(
    ref,
    fetchCount < fetchLimit ? fetchNextPage : () => { },
  );
  const fetchNext = React.useMemo(() => {
    if (fetchCount < fetchLimit) {
      return undefined;
    }
    return () => {
      setFetchCount(0);
      fetchNextPage();
    };
  }, [fetchNextPage]);

  const flattenedData = data.data?.pages.map((p) => getItems(p)).flat() ?? [];
  return {
    onScroll,
    flattenedData,
    fetchNext,
    isLoading: data.isLoading,
  };
}
